/**
 * Modal para editar categorías existentes.
 *
 * Este componente proporciona una interfaz de usuario para editar categorías
 * existentes. Incluye validación en tiempo real, detección de cambios para
 * habilitar el botón de guardar y formateo automático de texto.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import {
  Box,
  Divider,
  Icon,
  Modal,
  Typography,
  Card,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

/**
 * Modal para editar categorías existentes
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.open - Estado de apertura del modal
 * @param {Function} props.handleClose - Función para cerrar el modal
 * @param {Object} props.datosRecuperados - Datos originales de la categoría
 * @param {Object} props.datosEditados - Datos editados de la categoría
 * @param {boolean} props.isFormModified - Indica si el formulario ha sido modificado
 * @param {boolean} props.isDisabled - Estado de deshabilitación del formulario
 * @param {Function} props.getFieldValue - Función para obtener el valor de un campo
 * @param {Function} props.handleFieldChange - Función para manejar cambios en los campos
 * @param {Function} props.handleSubmit - Función para enviar el formulario
 * @param {Object} props.isFieldValid - Estado de validación de los campos
 */
export function CategoryEdit({
  open,
  handleClose,
  datosRecuperados,
  datosEditados,
  isFormModified,
  isDisabled,
  getFieldValue,
  handleFieldChange,
  handleSubmit,
  isFieldValid,
}) {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "400px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
  };

  // Monitorear cambios en el input y actualizar el estado del botón
  useEffect(() => {
    const fieldValue = getFieldValue("name") || "";
    const isValidField = isFieldValid?.name !== false;
    const hasChanges = isFormModified;

    setIsButtonDisabled(!fieldValue.trim() || !isValidField || !hasChanges);
  }, [getFieldValue, isFieldValid, isFormModified]);

  /**
   * Maneja los cambios en el input aplicando formato y validación
   * @param {string} fieldName - Nombre del campo
   * @param {string} value - Valor del campo
   */
  const handleInputChange = (fieldName, value) => {
    // Eliminar espacios al principio y capitalizar primera letra
    const formattedValue = value.trimStart();
    const capitalizedValue = formattedValue.charAt(0).toUpperCase() + formattedValue.slice(1);
    handleFieldChange(fieldName, capitalizedValue);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Card sx={style}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Typography id="modal-modal-title" component="h2">
            Editando categoría: {datosRecuperados.name || ""}
          </Typography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
              marginLeft: "auto",
            }}
            onClick={handleClose}
          >
            close
          </Icon>
        </MDBox>
        <Divider variant="middle" />
        <form onSubmit={handleSubmit}>
          <Box>
            <MDBox
              style={{
                display: "flex",
                justifyContent: "space-around",
                width: "100%",
              }}
            >
              <MDBox style={{ width: "100%" }}>
                <Typography
                  id="modal-modal-title"
                  component="h5"
                  style={{ marginTop: "5px", marginBottom: "10px" }}
                >
                  Nombre de la categoría
                </Typography>
                <MDInput
                  disabled={isDisabled}
                  style={{ width: "100%" }}
                  label="Nombre"
                  value={getFieldValue("name") || ""}
                  error={isFieldValid?.name === false}
                  helperText={
                    getFieldValue("name") === "" && isFieldValid?.name === false
                      ? "El campo no puede estar vacío"
                      : isFieldValid?.name === false
                        ? "No se permiten números ni caracteres especiales"
                        : ""
                  }
                  FormHelperTextProps={{
                    className: isFieldValid?.name === false ? "Mui-error" : ""
                  }}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                />
              </MDBox>
            </MDBox>
          </Box>
          <Divider variant="middle" />
          <MDBox
            style={{
              display: "flex",
              justifyContent: "space-around",
            }}
            m={-0.5}
            mb={-2}
          >
            <MDButton color="error" onClick={handleClose}>
              Cancelar
            </MDButton>
            <MDButton
              type="submit"
              onClick={handleSubmit}
              disabled={isDisabled || isButtonDisabled}
              color="success"
            >
              Guardar
            </MDButton>
          </MDBox>
        </form>
      </Card>
    </Modal>
  );
}

CategoryEdit.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  datosRecuperados: PropTypes.object,
  isDisabled: PropTypes.bool,
  datosEditados: PropTypes.object,
  getFieldValue: PropTypes.func,
  handleFieldChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  isFormModified: PropTypes.bool,
  isFieldValid: PropTypes.object,
};